import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { ArrowLeft, Clock, Calendar, Tag as TagIcon, Clipboard, ArrowRight } from 'lucide-react';
import { useEpisode } from '../hooks/useEpisode';
import { TerminalBlock } from '../components/Terminal';
import { DialogueBubble } from '../components/DialogueBubble';
import { getCategoryBadgeColor, getCategoryLabel } from '../utils/clipboardHelpers';

export const EpisodePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const contentRef = useRef<HTMLDivElement>(null);

  // Load episode dynamically with full content
  const { episode, loading, error } = useEpisode(slug || '');

  // Hydrate terminal blocks and dialogue bubbles after content is rendered
  useEffect(() => {
    if (!contentRef.current || !episode?.content) return;

    const roots: any[] = [];

    // Hydrate terminal blocks
    const terminalBlocks = contentRef.current.querySelectorAll('.terminal-block');
    terminalBlocks.forEach((block) => {
      const content = block.getAttribute('data-terminal-content');
      if (!content) return;

      // Unescape HTML entities
      const unescaped = content
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&amp;/g, '&');

      const root = createRoot(block);
      root.render(<TerminalBlock content={unescaped} />);
      roots.push(root);
    });

    // Hydrate dialogue bubbles
    const blockquotes = contentRef.current.querySelectorAll('blockquote');
    let dialogueCount = 0;

    blockquotes.forEach((blockquote) => {
      const text = blockquote.textContent || '';

      // Match pattern: **CHARACTER**: "dialogue"
      // Supports both **CHARACTER** and plain CHARACTER:
      const match = text.match(/(?:\*\*)?(\w+)(?:\*\*)?:\s*"([^"]+)"/);

      if (match) {
        const [_, character, dialogue] = match;
        const validChars = ['OPERATOR', 'PFY', 'USER', 'MANAGEMENT'];
        const normalizedChar = character.toUpperCase();

        if (validChars.includes(normalizedChar)) {
          // Mark this blockquote as a dialogue to remove default styling
          blockquote.classList.add('dialogue-blockquote');

          // Alternate sides for conversation flow
          const side = dialogueCount % 2 === 0 ? 'left' : 'right';
          dialogueCount++;

          // Replace blockquote with DialogueBubble component
          const root = createRoot(blockquote);
          root.render(
            <DialogueBubble
              character={normalizedChar as any}
              text={dialogue}
              side={side}
            />
          );
          roots.push(root);
        }
      }
    });

    // Cleanup function
    return () => {
      roots.forEach(root => root.unmount());
    };
  }, [episode?.content]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <div className="border-b border-gray-800">
          <div className="container mx-auto px-4 py-4">
            <div className="h-6 w-32 bg-gray-800 rounded animate-pulse"></div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="h-12 bg-gray-800 rounded mb-6 animate-pulse"></div>
            <div className="flex items-center gap-6 mb-8">
              <div className="h-4 w-32 bg-gray-800 rounded animate-pulse"></div>
              <div className="h-4 w-24 bg-gray-800 rounded animate-pulse"></div>
            </div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-800 rounded animate-pulse"></div>
              <div className="h-4 bg-gray-800 rounded animate-pulse"></div>
              <div className="h-4 bg-gray-800 rounded w-3/4 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !episode) {
    return (
      <div className="min-h-screen bg-black">
        <div className="border-b border-gray-800">
          <div className="container mx-auto px-4 py-4">
            <Link
              to="/episodes"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Episodes
            </Link>
          </div>
        </div>
        <div className="container mx-auto px-4 py-24">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-8xl mb-6">404</div>
            <h1 className="text-4xl font-bold text-gray-100 mb-4">Episode Not Found</h1>
            <p className="text-gray-400 mb-8">
              {error ? error.message : "The episode you're looking for doesn't exist."}
            </p>
            <Link to="/episodes" className="btn btn-primary">
              Browse All Episodes
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Remove any manual initialization code since useEpisode already handles everything
  const { title, author, date, readingTime, tags, excerpt, content, featured, thumbnail } = episode;

  return (
    <div className="min-h-screen bg-black">
      {/* Header with back button */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/episodes"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Episodes
          </Link>
        </div>
      </div>

      <article className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Episode Header */}
          <header className="mb-12">
            {featured && (
              <span className="inline-block px-3 py-1 mb-4 text-sm font-medium text-amber-500 bg-amber-500/10 rounded-full border border-amber-500/30">
                ⭐ Featured Episode
              </span>
            )}

            <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-6">
              {title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <time>
                  {new Date(date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </time>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{readingTime} min read</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-gray-500">By</span>
                <span className="text-red-500 font-medium">{author}</span>
              </div>
            </div>

            {excerpt && (
              <p className="text-xl text-gray-300 leading-relaxed border-l-4 border-red-500 pl-6">
                {excerpt}
              </p>
            )}
          </header>

          {/* Episode Thumbnail */}
          {thumbnail && (
            <div className="mb-12">
              <img
                src={thumbnail}
                alt={`${title} thumbnail`}
                className="w-full rounded-lg shadow-2xl"
              />
            </div>
          )}

          {/* Episode Content */}
          <div
            ref={contentRef}
            className="prose prose-invert prose-red max-w-none
                       prose-headings:text-gray-100 prose-headings:font-bold
                       prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                       prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                       prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
                       prose-a:text-red-500 prose-a:no-underline hover:prose-a:text-red-400
                       prose-strong:text-gray-100 prose-strong:font-semibold
                       prose-code:text-teal-400 prose-code:bg-gray-900 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                       prose-pre:bg-gray-900 prose-pre:border prose-pre:border-gray-800
                       prose-blockquote:border-l-4 prose-blockquote:border-red-500 prose-blockquote:pl-6 prose-blockquote:italic
                       prose-ul:text-gray-300 prose-ol:text-gray-300
                       prose-li:my-2"
            dangerouslySetInnerHTML={{ __html: content || '' }}
          />

          {/* Tags */}
          {tags && tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-800">
              <div className="flex items-center gap-3 flex-wrap">
                <TagIcon className="w-5 h-5 text-gray-500" />
                {tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/episodes?tag=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 text-sm bg-gray-900 text-gray-300 rounded-full
                             hover:bg-red-500 hover:text-white transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Clipboard Note Reference */}
          {episode.clipboardNote && episode.clipboardCategory && (
            <div className="mt-8 pt-6 border-t border-gray-800/50">
              <div className="flex items-center gap-3 flex-wrap text-sm">
                <Clipboard className="w-4 h-4 text-teal-500" />
                <span className="text-gray-400">Note added to Clipboard</span>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${getCategoryBadgeColor(episode.clipboardCategory)}`}>
                  {getCategoryLabel(episode.clipboardCategory)}
                </span>
                <Link
                  to="/tools/clipboard"
                  className="inline-flex items-center gap-1 text-teal-500 hover:text-teal-400 font-medium transition-colors ml-auto"
                >
                  View in Clipboard
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {/* Navigation Footer */}
          <div className="mt-16 pt-8 border-t border-gray-800">
            <Link
              to="/episodes"
              className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to All Episodes
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};
